import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { UserRound } from "lucide-react";
import toast from "react-hot-toast";
import { adminService } from "@/services/adminService";
import { salesAgentService } from "@/services/salesAgentService";
import { AdminUserStatusDot } from "@/components/admin/users/AdminUserStatusIndicators";
import { PickerPanel } from "@/components/common";
import {
  Button,
  Modal,
  ModalFooter,
  PercentInput,
  Spinner,
  TruncatedText,
} from "@/components/ui";
import { toastApiError } from "@/lib/errorMessages";
import {
  commissionPercentStringFromRate,
  formatCommissionPercentLabel,
  rateFromPercentInput,
} from "@/lib/commissionRate";
import { StaffConfigRow } from "./StaffConfigRow";
import { matchesTextQuery } from "@/lib/textSearch";
import { queryLoadFailure } from "@/lib/queryLoadFailure";
import { formatDate } from "@/lib/datetime";

interface AdminActivitySalesAgentSectionProps {
  activityId: string;
}

function assignmentQueryKey(activityId: string) {
  return ["admin", "activity", activityId, "sales-agent-assignment"] as const;
}

export function AdminActivitySalesAgentSection({
  activityId,
}: AdminActivitySalesAgentSectionProps) {
  const { t } = useTranslation("activities");
  const { t: tCommon } = useTranslation("common");
  const queryClient = useQueryClient();
  const [assignModalOpen, setAssignModalOpen] = useState(false);

  const { data: assignment, isLoading } = useQuery({
    queryKey: assignmentQueryKey(activityId),
    queryFn: () => adminService.getActivitySalesAgentAssignment(activityId),
    enabled: !!activityId,
  });

  const invalidateAssignment = () => {
    void queryClient.invalidateQueries({
      queryKey: assignmentQueryKey(activityId),
    });
    void queryClient.invalidateQueries({ queryKey: ["admin", "sales-agents"] });
    if (assignment?.sales_agent_user_id) {
      void queryClient.invalidateQueries({
        queryKey: [
          "admin",
          "sales-agents",
          assignment.sales_agent_user_id,
          "scoped-assignments",
        ],
      });
    }
  };

  const upsertMutation = useMutation({
    mutationFn: async ({
      salesAgentUserId,
      commissionPct,
    }: {
      salesAgentUserId: string;
      commissionPct: string;
    }) => {
      const rate = rateFromPercentInput(commissionPct.trim());
      if (rate == null) {
        throw new Error("invalid_rate");
      }
      await adminService.upsertSalesAgentActivityAssignment({
        activity_id: activityId,
        sales_agent_user_id: salesAgentUserId,
        commission_rate: rate,
      });
    },
    onSuccess: () => {
      toast.success(t("detail.adminSalesAgentToastSaved"));
      invalidateAssignment();
      setAssignModalOpen(false);
    },
    onError: (err: unknown) => {
      if (err instanceof Error && err.message === "invalid_rate") {
        toast.error(t("detail.adminSalesAgentPercentInvalid"));
        return;
      }
      toastApiError(err);
    },
  });

  const actionLabel = assignment
    ? t("detail.adminSalesAgentChange")
    : t("detail.adminSalesAgentAssign");

  return (
    <>
      <StaffConfigRow
        icon={UserRound}
        title={t("detail.adminSalesAgentTitle")}
        hint={t("detail.adminSalesAgentHint")}
        actions={
          !isLoading ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setAssignModalOpen(true)}
            >
              {actionLabel}
            </Button>
          ) : undefined
        }
      >
        {isLoading ? (
          <Spinner
            size="sm"
            variant="muted"
            loadingLabel={tCommon("loading")}
          />
        ) : assignment ? (
          <>
            <AdminUserStatusDot isActive={assignment.sales_agent_is_active} />
            <div className="min-w-0 text-right">
              <Link
                to={`/admin/sales-agents/${assignment.sales_agent_user_id}`}
                className="block max-w-[14rem] hover:underline sm:max-w-xs"
              >
                <TruncatedText className="text-primary-700 text-sm font-medium">
                  {assignment.sales_agent_email}
                </TruncatedText>
              </Link>
              <p className="text-surface-500 text-xs tabular-nums">
                {formatCommissionPercentLabel(assignment.commission_rate)}
                <span
                  className="text-surface-300"
                  aria-hidden
                >
                  {" · "}
                </span>
                {formatDate(assignment.assigned_at)}
              </p>
            </div>
          </>
        ) : (
          <span className="text-surface-500 text-sm">
            {t("detail.adminSalesAgentEmpty")}
          </span>
        )}
      </StaffConfigRow>

      <AssignSalesAgentModal
        isOpen={assignModalOpen}
        onClose={() => setAssignModalOpen(false)}
        initialSalesAgentUserId={assignment?.sales_agent_user_id}
        initialCommissionPct={
          assignment
            ? commissionPercentStringFromRate(assignment.commission_rate)
            : ""
        }
        isPending={upsertMutation.isPending}
        onSave={(salesAgentUserId, commissionPct) =>
          upsertMutation.mutate({ salesAgentUserId, commissionPct })
        }
      />
    </>
  );
}

function AssignSalesAgentModal({
  isOpen,
  onClose,
  initialSalesAgentUserId,
  initialCommissionPct,
  isPending,
  onSave,
}: {
  isOpen: boolean;
  onClose: () => void;
  initialSalesAgentUserId?: string;
  initialCommissionPct: string;
  isPending: boolean;
  onSave: (salesAgentUserId: string, commissionPct: string) => void;
}) {
  const { t } = useTranslation("activities");
  const { t: tCommon } = useTranslation("common");

  return (
    <Modal
      closeLabel={tCommon("close")}
      isOpen={isOpen}
      onClose={onClose}
      title={
        initialSalesAgentUserId
          ? t("detail.adminSalesAgentModalReassignTitle")
          : t("detail.adminSalesAgentModalAssignTitle")
      }
      size="sm"
    >
      {isOpen ? (
        <AssignSalesAgentModalForm
          key={`${initialSalesAgentUserId ?? ""}:${initialCommissionPct}`}
          initialSalesAgentUserId={initialSalesAgentUserId}
          initialCommissionPct={initialCommissionPct}
          isPending={isPending}
          onClose={onClose}
          onSave={onSave}
        />
      ) : null}
    </Modal>
  );
}

function AssignSalesAgentModalForm({
  initialSalesAgentUserId,
  initialCommissionPct,
  isPending,
  onClose,
  onSave,
}: {
  initialSalesAgentUserId?: string;
  initialCommissionPct: string;
  isPending: boolean;
  onClose: () => void;
  onSave: (salesAgentUserId: string, commissionPct: string) => void;
}) {
  const { t } = useTranslation("activities");
  const { t: tCommon } = useTranslation("common");
  const { t: tErrors } = useTranslation("errors");
  const [salesAgentSearch, setSalesAgentSearch] = useState("");
  const [salesAgentUserId, setSalesAgentUserId] = useState(
    initialSalesAgentUserId ?? "",
  );
  const [commissionPct, setCommissionPct] = useState(initialCommissionPct);

  const salesAgentsQuery = useQuery({
    queryKey: ["admin", "sales-agents", "activity-assign-picker"],
    queryFn: () => salesAgentService.listForAdmin(),
  });
  const { data: salesAgents = [] } = salesAgentsQuery;

  const filteredAgents = useMemo(() => {
    if (!salesAgentSearch.trim()) return salesAgents;
    return salesAgents.filter((agent) =>
      matchesTextQuery(salesAgentSearch, [agent.email, agent.user_id]),
    );
  }, [salesAgentSearch, salesAgents]);

  const canSave =
    !!salesAgentUserId && rateFromPercentInput(commissionPct.trim()) != null;

  return (
    <>
      <div className="space-y-3">
        <PickerPanel
          searchPlaceholder={t("createModal.salesAgentSearchPlaceholder")}
          searchValue={salesAgentSearch}
          onSearchChange={setSalesAgentSearch}
          isLoading={salesAgentsQuery.isLoading}
          loadError={queryLoadFailure(
            salesAgentsQuery,
            tErrors("loadSalesAgents"),
          )}
          listClassName="max-h-44"
          emptyMessage={
            salesAgentSearch.trim()
              ? t("createModal.salesAgentNoResults")
              : t("createModal.salesAgentListEmpty")
          }
          items={filteredAgents.map((agent) => ({
            id: agent.user_id,
            title: agent.email,
            selected: salesAgentUserId === agent.user_id,
            onSelect: () => {
              setSalesAgentUserId(agent.user_id);
              setSalesAgentSearch(agent.email);
            },
          }))}
        />
        <PercentInput
          id="activity-sales-agent-commission"
          label={t("createModal.salesAgentCutPercentLabel")}
          value={commissionPct}
          onChange={(e) => setCommissionPct(e.target.value)}
          required
        />
      </div>

      <ModalFooter>
        <Button
          type="button"
          variant="ghost"
          onClick={onClose}
          disabled={isPending}
        >
          {tCommon("cancel")}
        </Button>
        <Button
          type="button"
          onClick={() => onSave(salesAgentUserId, commissionPct)}
          isLoading={isPending}
          disabled={!canSave}
        >
          {tCommon("save")}
        </Button>
      </ModalFooter>
    </>
  );
}
